import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const initialState = {
  jobPosts: [],
  jobPost: null,
  myCompanyJobs: [],
  myCompanyJobsTotal: 0,
  loading: false,
  isSuccess: false,
  message: '',
  errors: []
};

// Get all job posts (authenticated users)
export const fetchJobPosts = createAsyncThunk(
  'jobPosts/fetchJobPosts',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/job-posts');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        console.log(error);
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Get my company jobs (admin/manager/responsible dashboard)
export const fetchMyCompanyJobs = createAsyncThunk(
  'jobPosts/fetchMyCompanyJobs',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/job-posts/my-company', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Get job post by ID (public)
export const fetchJobPostById = createAsyncThunk(
  'jobPosts/fetchJobPostById',
  async (jobId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/job-posts/${jobId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Get active company jobs (OPEN status only for public profile)
export const fetchActiveCompanyJobs = createAsyncThunk(
  'jobPosts/fetchActiveCompanyJobs',
  async (companyId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/job-posts/company/${companyId}/active`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Get all company jobs (for compatibility)
export const fetchJobsByCompany = createAsyncThunk(
  'jobPosts/fetchJobsByCompany',
  async (companyId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/job-posts/company/${companyId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Create job post (admin/manager/responsible)
export const createJobPost = createAsyncThunk(
  'jobPosts/createJobPost',
  async (jobData, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/job-posts/', jobData);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Update job post (creator only)
export const updateJobPost = createAsyncThunk(
  'jobPosts/updateJobPost',
  async ({ jobId, jobData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.patch(`/job-posts/${jobId}`, jobData);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// Delete job post (creator only)
export const deleteJobPost = createAsyncThunk(
  'jobPosts/deleteJobPost',
  async (jobId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/job-posts/${jobId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

const jobPostsSlice = createSlice({
  name: 'jobPosts',
  initialState,
  reducers: {
    clearJobPost: (state) => {
      state.jobPost = null;
    },
    clearErrors: (state) => {
      state.errors = [];
    }
  },
  extraReducers: {
    // Fetch all job posts
    [fetchJobPosts.pending]: (state) => {
      state.loading = true;
    },
    [fetchJobPosts.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.jobPosts = payload || [];
      state.loading = false;
    },
    [fetchJobPosts.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    // Fetch my company jobs
    [fetchMyCompanyJobs.pending]: (state) => {
      state.loading = true;
    },
    [fetchMyCompanyJobs.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.myCompanyJobs = payload?.data || [];
      state.myCompanyJobsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [fetchMyCompanyJobs.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    // Fetch job post by ID
    [fetchJobPostById.pending]: (state) => {
      state.loading = true;
    },
    [fetchJobPostById.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.jobPost = payload;
      state.loading = false;
    },
    [fetchJobPostById.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    // Fetch active company jobs
    [fetchActiveCompanyJobs.pending]: (state) => {
      state.loading = true;
    },
    [fetchActiveCompanyJobs.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.jobPosts = payload || [];
      state.loading = false;
    },
    [fetchActiveCompanyJobs.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    // Fetch jobs by company
    [fetchJobsByCompany.pending]: (state) => {
      state.loading = true;
    },
    [fetchJobsByCompany.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.jobPosts = payload || [];
      state.loading = false;
    },
    [fetchJobsByCompany.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    // Create job post
    [createJobPost.pending]: (state) => {
      state.loading = true;
    },
    [createJobPost.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.message = 'Job post created successfully';
      state.jobPost = payload;
      state.myCompanyJobs.push(payload); // ✅ Add this to instantly update list in dashboard
      state.loading = false;
    },
    [createJobPost.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    // Update job post
    [updateJobPost.pending]: (state) => {
      state.loading = true;
    },
    [updateJobPost.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.message = 'Job post updated successfully';
      state.jobPost = payload;
      state.loading = false;
    },
    [updateJobPost.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    // Delete job post
    [deleteJobPost.pending]: (state) => {
      state.loading = true;
    },
    [deleteJobPost.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.message = payload?.message || 'Job post deleted successfully';
      state.loading = false;
    },
    [deleteJobPost.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      } else {
        state.errors = ['Error deleting job post'];
      }
      state.isSuccess = false;
      state.loading = false;
    }
  }
});

export const { clearJobPost, clearErrors } = jobPostsSlice.actions;
export default jobPostsSlice.reducer;
